import dayjs from 'dayjs';
import { StatusChip } from '..';
import { ReactNode } from 'react';
import { Book } from 'pages/dashboard';
import { renderFormattedDate } from 'utils';
import CloseIcon from '@mui/icons-material/Close';
import { FormattedMessage, useIntl } from 'react-intl';
import { EffortItem, Project, User } from 'library/types';
import { Box, Chip, Stack, Drawer, Divider, Typography, IconButton, Toolbar } from '@mui/material';

type DetailItem = { label: string; value: ReactNode };

type DetailDrawerProps = {
  users: User[];
  books: Book[];
  products: Project[];
  open: boolean;
  close: () => void;
  detailRow: EffortItem | null;
};

const DetailDrawer = ({ users, books, products, open, close, detailRow }: DetailDrawerProps) => {
  const intl = useIntl();

  const renderRowDetails = (row: EffortItem | null): DetailItem[] => {
    if (!row) return [];

    return [
      {
        label: intl.formatMessage({ id: 'effortTracing.uniqueKey', defaultMessage: 'Efor Takip No' }),
        value: row.trackNo ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'effortTracing.createdAt', defaultMessage: 'Oluşturulma Tarihi' }),
        value: row.createdAt ? renderFormattedDate(dayjs(row.createdAt)) : '-',
      },
      {
        label: intl.formatMessage({ id: 'effortTracing.consultantEmail', defaultMessage: 'Talebi Açan Danışman' }),
        value: users.find((u) => u.id === row.consultantId)?.nameSurname ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'effortTracing.appointedConsultantEmail', defaultMessage: 'Atanan Danışman' }),
        value: users.find((u) => u.id === row.appointedConsultantId)?.nameSurname ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'effortTracing.bookId', defaultMessage: 'Proje Seçimi' }),
        value: books.find((b) => b.id === row.bookId)?.companyName ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'effortTracing.companyTitle', defaultMessage: 'Talep Eden Şirket' }),
        value: (() => {
          const book = books.find((b) => b.id === row.bookId);
          const company = book?.companies?.find((c) => c.vkn === row.companyVkn);
          return company?.title ?? '-';
        })(),
      },
      {
        label: intl.formatMessage({ id: 'effortTracing.projectId', defaultMessage: 'İlgili Ürün' }),
        value: products.find((p) => p.id === row.projectId)?.title ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'effortTracing.explanation', defaultMessage: 'Efor Açıklaması' }),
        value: row.explanation ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'effortTracing.effortDay', defaultMessage: 'Efor Günü' }),
        value: row.effortDay ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'effortTracing.estimatedStartDate', defaultMessage: 'Tahmini Başlangıç' }),
        value: row.estimatedStartDate ? renderFormattedDate(dayjs(row.estimatedStartDate)) : '-',
      },
      {
        label: intl.formatMessage({ id: 'effortTracing.estimatedEndDate', defaultMessage: 'Tahmini Bitiş' }),
        value: row.estimatedEndDate ? renderFormattedDate(dayjs(row.estimatedEndDate)) : '-',
      },
      {
        label: intl.formatMessage({ id: 'effortTracing.confirmationDate', defaultMessage: 'Onay/Ret Tarihi' }),
        value: row.confirmationDate ? renderFormattedDate(dayjs(row.confirmationDate)) : '-',
      },
      {
        label: intl.formatMessage({
          id: 'effortTracing.customerFullName',
          defaultMessage: 'Talep Eden Müşteri Ad Soyad',
        }),
        value: row.customerFullName ?? '-',
      },
      {
        label: intl.formatMessage({
          id: 'effortTracing.customerEmail',
          defaultMessage: 'Talep Eden Müşteri E-Posta',
        }),
        value: row.customerEmail ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'effortTracing.consultantNote', defaultMessage: 'Danışman Notu' }),
        value: row.consultantNote ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'effortTracing.isSasNecessary', defaultMessage: 'SAS Zorunlu mu?' }),
        value: row.isSasNecessary ? <Chip size="small" label="Evet" /> : <Chip size="small" label="Hayır" />,
      },
      { label: intl.formatMessage({ id: 'effortTracing.sasNo', defaultMessage: 'SAS No' }), value: row.sasNo ?? '-' },
      {
        label: intl.formatMessage({ id: 'effortTracing.invoiceNote', defaultMessage: 'Fatura Notu' }),
        value: row.invoiceNote ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'confirmEffort.companyVknForInvoice', defaultMessage: 'Fatura Kesilecek VKN' }),
        value: row.companyVknForInvoice ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'effortTracing.invoiceNo', defaultMessage: 'Fatura No' }),
        value: row.invoiceNo ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'effortTracing.invoiceDate', defaultMessage: 'Fatura Tarihi' }),
        value: row.invoiceDate ? renderFormattedDate(dayjs(row.invoiceDate)) : '-',
      },
      {
        label: intl.formatMessage({ id: 'effortTracing.status', defaultMessage: 'Statü' }),
        value: <StatusChip status={Number(row.status)} showNextHint={false} />,
      },
    ];
  };

  return (
    <Drawer anchor="right" open={open} onClose={close}>
      <Toolbar />
      <Box sx={{ width: { xs: '92vw', sm: 520 }, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">
            <FormattedMessage id="common.detail" defaultMessage="Detay" />
          </Typography>
          <IconButton onClick={close}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
          {detailRow?.trackNo ?? ''}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Stack spacing={1.25}>
          {renderRowDetails(detailRow).map((item) => (
            <Box key={item.label}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {item.label}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Drawer>
  );
};

export default DetailDrawer;
